
/**
 * Classe base para erros da aplicação
 */

export class AppError extends Error {
  constructor(
    public code: string,
    message: string,
    public statusCode: number = 500
  ) {
    super(message)
    this.name = 'AppError'
  }
}

export class ValidationError extends AppError {
  constructor(message: string) {
    super('VALIDATION_ERROR', message, 400)
    this.name = 'ValidationError'
  }
}

export class NotFoundError extends AppError {
  constructor(resource: string) {
    super('NOT_FOUND', `${resource} não encontrado`, 404)
    this.name = 'NotFoundError'
  }
}

export class UnauthorizedError extends AppError {
  constructor() {
    super('UNAUTHORIZED', 'Não autorizado', 401)
    this.name = 'UnauthorizedError'
  }
}

export class SupabaseError extends AppError {
  constructor(message: string, public originalError: unknown) {
    super('SUPABASE_ERROR', message, 500)
    this.name = 'SupabaseError'
  }
}

/**
 * Verificar se é AppError
 */
export function isAppError(error: unknown): error is AppError {
  return error instanceof AppError
}

/**
 * Extrair mensagem de erro
 */
export function getErrorMessage(error: unknown): string {
  if (error instanceof AppError) {
    return error.message
  }
  if (error instanceof Error) {
    return error.message
  }
  return 'Erro desconhecido'
}

const TECHNICAL_ERROR_PATTERNS = [
  /supabase/i,
  /stripe/i,
  /http\s?\d{3}/i,
  /unauthorized/i,
  /authorization/i,
  /missing/i,
  /function/i,
  /edge/i,
  /jwt/i,
  /token/i,
  /postgres/i,
  /database/i,
  /\bdb\b/i,
  /schema/i,
  /column/i,
  /relation/i,
  /permission denied/i,
  /row-level/i,
  /duplicate key/i,
  /violates/i,
  /constraint/i,
  /does not exist/i,
  /invalid signature/i,
  /status_code/i,
  /api/i,
]

export function getPublicErrorMessage(message: unknown, fallback = 'Não foi possível concluir a ação agora. Tente novamente.'): string {
  const raw = typeof message === 'string' ? message.trim() : ''
  if (!raw) return fallback

  return TECHNICAL_ERROR_PATTERNS.some((pattern) => pattern.test(raw)) ? fallback : raw
}

type SupabaseFunctionErrorContext = {
  json?: () => Promise<{ message?: string; error?: string }>
}

type SupabaseFunctionErrorLike = {
  message?: string
  context?: SupabaseFunctionErrorContext
}

export async function getSupabaseFunctionErrorMessage(
  error: unknown,
  fallback = 'Erro inesperado'
): Promise<string> {
  const baseMessage =
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof error.message === 'string'
      ? error.message
      : fallback

  try {
    const context = (error as SupabaseFunctionErrorLike).context
    const body = await context?.json?.()
    return body?.message || body?.error || baseMessage
  } catch {
    return baseMessage
  }
}

export async function getPublicSupabaseFunctionErrorMessage(
  error: unknown,
  fallback = 'Não foi possível concluir a ação agora. Tente novamente.'
): Promise<string> {
  const message = await getSupabaseFunctionErrorMessage(error, fallback)
  return getPublicErrorMessage(message, fallback)
}
